import { cn } from "@/flokit/utils/styling/cn";
import { cx } from "class-variance-authority";
import get from "lodash/get";
import React from "react";
import { When } from "../when";

export const HorizontalList = (props: {
  /**
   * Note: Even if the element returns `null` from its render function, it will still result in an entry in the horizontal list. https://stackoverflow.com/a/33219433
   */
  items: React.ReactNode[];
  itemClass?: string;
  divider?: "dot" | "slash";
  ariaLabel: string;
  className?: string;
}) => {
  const divider = get(props, "divider", "dot");
  return (
    <ul
      className={cn("flex flex-wrap items-center gap-x-2", props.className)}
      aria-label={props.ariaLabel}
    >
      {props.items
        // See note above
        ?.filter((item) => item != null)
        ?.map((item, index) => {
          const shouldShowDivider = index < props.items.length - 1;

          return (
            <React.Fragment key={index}>
              <li
                key={index}
                className={cx("flex items-center", props.itemClass)}
              >
                {item}
              </li>
              <When condition={shouldShowDivider && divider === "dot"}>
                <span
                  style={{ width: 3, height: 3 }}
                  aria-hidden={true}
                  className="block rounded-full bg-portland-gray"
                />
              </When>
              <When condition={shouldShowDivider && divider === "slash"}>
                <span aria-hidden={true} className="block text-portland-gray">
                  /
                </span>
              </When>
            </React.Fragment>
          );
        })}
    </ul>
  );
};

export const RowList = (props: {
  children: React.ReactNode;
  divider?: "dot" | "slash";
  ariaLabel: string;
  className?: string;
}) => {
  const divider = props.divider || "dot";
  return (
    <ul
      className={cn(
        "flex flex-wrap items-center gap-x-2 text-base",
        props.className,
      )}
      aria-label={props.ariaLabel}
    >
      {React.Children.toArray(props.children)
        .filter((c) => c != null)
        .map((child, index) => (
          <React.Fragment key={index}>
            {index > 0 && divider === "dot" && (
              <li
                className="h-[3px] w-[3px] rounded-full bg-portland-gray"
                aria-hidden="true"
                role="presentation"
              />
            )}
            {index > 0 && divider === "slash" && (
              <li
                className="text-portland-gray"
                aria-hidden="true"
                role="presentation"
              >
                /
              </li>
            )}
            <li className="flex-shrink-0">{child}</li>
          </React.Fragment>
        ))}
    </ul>
  );
};
